import { confirmIntelligenceNavigationAway } from '../features/shared/intelligence-navigation';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../components/Icons';
import { ThemePreferenceSelector, useThemePreference } from '../components/ThemeToggle';
import { useWorkspaceAccount } from '../components/WorkspaceAccountContext';
import type { PersonaDefinition, PersonaId } from '../types/persona';
import type { WorkspaceNavigationItem } from '../features/shared/types';
import { useIOSPresentation, standardPath } from '../lib/web-presentation';

/** Native modal semantics provide focus containment, Escape and inert background. */
export function IOSSheet({ title, children, onClose }: { title: string; children: ReactNode; onClose(): void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current!;
    const previous = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = 'hidden';
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  return createPortal(<dialog aria-labelledby={titleId} className="ios-sheet" ref={ref}
    onCancel={(event) => { event.preventDefault(); onClose(); }}
    onKeyDown={(event) => {
      if (event.key !== 'Tab') return;
      const controls = [...event.currentTarget.querySelectorAll<HTMLElement>(
        'button, a[href], input, select, textarea, [tabindex="0"]')]
        .filter((element) => !element.matches(':disabled') && element.getClientRects().length > 0);
      const first = controls[0]; const last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }}
    onClick={(event) => { if (event.target === event.currentTarget) {
      const box = event.currentTarget.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) onClose();
    } }}>
    <header><h2 id={titleId}>{title}</h2><button className="ios-text-button" onClick={onClose} type="button">Done</button></header>
    <div className="ios-sheet__body">{children}</div>
  </dialog>, document.body);
}

export function IOSDisclosure({ children, title, open = false }: { children: ReactNode; title: ReactNode; open?: boolean }) {
  return <details className="ios-disclosure" open={open || undefined}
    onInvalidCapture={(event) => { event.currentTarget.open = true; }}>
    <summary><span>{title}</span><Icon name="chevron-down" size={18} /></summary>
    <div className="ios-disclosure__body">{children}</div>
  </details>;
}

export function IOSOptionalDetails(props: { children: ReactNode; title: ReactNode; open?: boolean }) {
  const ios = useIOSPresentation();
  return ios ? <IOSDisclosure {...props} /> : <>{props.children}</>;
}

export function IOSAccount({ activeProduct, availableProducts, onProductChange, onSignOut, name, email }: {
  activeProduct: PersonaDefinition; availableProducts: readonly PersonaDefinition[];
  onProductChange?: (id: PersonaId) => void; onSignOut(): void; name: string; email?: string;
}) {
  useThemePreference();
  const [open, setOpen] = useState(false);
  return <><button aria-label="Account and products" aria-haspopup="dialog" className="ios-account-button"
    onClick={() => setOpen(true)} type="button"><Icon name="users" size={21} /></button>
    {open && <IOSSheet title="Account" onClose={() => setOpen(false)}>
      <div className="ios-account-identity"><strong>{name}</strong>{email && <span>{email}</span>}</div>
      {availableProducts.length > 1 && onProductChange && <nav aria-label="Product experience" className="ios-group">
        {availableProducts.map((product) => <button aria-current={product.id === activeProduct.id ? 'page' : undefined}
          key={product.id} onClick={() => { if (!confirmIntelligenceNavigationAway()) return; onProductChange(product.id); setOpen(false); }} type="button">
          <span>{product.shortLabel}</span>{product.id === activeProduct.id && <Icon name="check" size={20} />}
        </button>)}
      </nav>}
      <ThemePreferenceSelector />
      <div className="ios-group"><a onClick={(event) => { if (!confirmIntelligenceNavigationAway()) event.preventDefault(); }} href={`${standardPath()}${window.location.search}${window.location.hash}`}>Open standard web version<Icon name="chevron-right" size={18} /></a>
        <button className="ios-destructive" onClick={() => { setOpen(false); onSignOut(); }} type="button">Sign out<Icon name="log-out" size={18} /></button></div>
    </IOSSheet>}
  </>;
}

export function IOSNavigation<T extends string>({ active, items, label = 'Workspace sections', mobileSettings, onChange }: {
  active: T; items: readonly WorkspaceNavigationItem<T>[]; label?: string;
  mobileSettings?: { active: boolean; onOpen(): void }; onChange(view: T): void;
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const account = useWorkspaceAccount();
  const current = mobileSettings?.active ? 'Settings' : items.find((item) => item.id === active)?.label ?? 'Workspace';
  const previous = useRef(current);
  useEffect(() => {
    if (previous.current === current) return;
    previous.current = current;
    const frame = window.requestAnimationFrame(() => {
      const heading = document.querySelector<HTMLElement>('#product-content h1');
      heading?.setAttribute('tabindex', '-1');
      heading?.focus({ preventScroll: true });
      window.scrollTo({ top: 0, behavior: 'instant' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [current]);
  const select = (id: T) => { setMoreOpen(false); onChange(id); };
  const itemButton = (item: WorkspaceNavigationItem<T>, mode: 'sidebar' | 'tab' | 'sheet' = 'sidebar') => <button
    className="ios-nav-item"
    aria-current={!mobileSettings?.active && item.id === active ? 'page' : undefined}
    aria-label={`${item.label}${item.count ? `. ${item.count} items` : ''}`}
    key={item.id} onClick={() => select(item.id)} type="button">
    <span className="ios-nav-icon"><Icon name={item.icon ?? 'file-text'} size={22} />
      {!!item.count && mode === 'tab' && <b className="ios-nav-dot" />}</span>
    <span className="ios-nav-label">{item.label}{mode !== 'tab' && <small>{item.description}</small>}</span>
    {mode !== 'tab' && <span className="ios-nav-accessory">{!!item.count && <b className="ios-nav-count">{item.count}</b>}
      {mode === 'sheet' && <Icon name="chevron-right" size={18} />}</span>}
  </button>;
  return <>
    <nav aria-label={`${label} sidebar`} className="ios-sidebar">
      <p className="ios-sidebar-label">Workspace</p>
      {items.map((item) => itemButton(item))}
      {mobileSettings && <button aria-current={mobileSettings.active ? 'page' : undefined} onClick={mobileSettings.onOpen} type="button">
        <Icon name="settings" size={22} /><span>Settings</span></button>}
      {account && <div className="ios-sidebar-account"><strong>{account.displayName}</strong><small>{account.capacity}</small></div>}
    </nav>
    <nav aria-label={label} className="ios-tabbar">
      {items.slice(0, 4).map((item) => itemButton(item, 'tab'))}
      <button aria-current={mobileSettings?.active || items.slice(4).some((item) => item.id === active) ? 'page' : undefined}
        aria-label={`More sections${items.slice(4).some((item) => item.id === active) || mobileSettings?.active ? `. Current section: ${current}` : ''}`}
        aria-haspopup="dialog" onClick={() => setMoreOpen(true)} type="button"><Icon name="menu" size={22} /><span>More</span></button>
    </nav>
    <span aria-live="polite" className="am-visually-hidden">{current}</span>
    {moreOpen && <IOSSheet title="More" onClose={() => setMoreOpen(false)}><nav aria-label="More workspace sections" className="ios-group ios-more-menu">
      {items.slice(4).map((item) => itemButton(item, 'sheet'))}
      {mobileSettings && <button className="ios-nav-item" aria-current={mobileSettings.active ? 'page' : undefined} onClick={() => {
        setMoreOpen(false); mobileSettings.onOpen();
      }} type="button"><span className="ios-nav-icon"><Icon name="settings" size={22} /></span>
        <span className="ios-nav-label">Settings<small>Account and preferences</small></span>
        <span className="ios-nav-accessory"><Icon name="chevron-right" size={18} /></span></button>}
    </nav></IOSSheet>}
  </>;
}

export function IOSContext({ contextLabel, contextValue, contextOptions, onContextChange, periodLabel, periodValue,
  periodOptions, onPeriodChange, actionLabel, actionAccessibleLabel, onAction }: {
  contextLabel: string; contextValue: string; contextOptions?: readonly { label: string; value: string }[];
  onContextChange?: (value: string) => void; periodLabel: string; periodValue: string;
  periodOptions?: readonly { label: string; value: string }[]; onPeriodChange?: (value: string) => void;
  actionLabel?: string; actionAccessibleLabel?: string; onAction?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const context = contextOptions?.find((option) => option.value === contextValue)?.label ?? contextValue;
  const period = periodOptions?.find((option) => option.value === periodValue)?.label ?? periodValue;
  const field = (label: string, value: string, options?: readonly { label: string; value: string }[], onChange?: (value: string) => void) =>
    <label className="ios-context-field"><span>{label}</span>{options && onChange
      ? <select aria-label={`Active ${label.toLowerCase()}`} value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select>
      : <strong>{value}</strong>}</label>;
  return <><button aria-label={`Tax context: ${context}, ${period}`} aria-haspopup="dialog" className="ios-context-button"
    onClick={() => setOpen(true)} type="button"><span><strong>{context}</strong><small>{period}</small></span><Icon name="chevron-down" size={16} /></button>
    {open && <IOSSheet title="Tax context" onClose={() => setOpen(false)}>
      <div className="ios-group">{field(contextLabel, contextValue, contextOptions, onContextChange)}
        {field(periodLabel, periodValue, periodOptions, onPeriodChange)}</div>
      {onAction && actionLabel && <button aria-label={actionAccessibleLabel} className="ios-text-button" onClick={() => { setOpen(false); onAction(); }} type="button">+ {actionLabel}</button>}
    </IOSSheet>}</>;
}
